/**
 * 改写版本标签 `edition_label` 的入参形态（ADM-BOK-S024，前后端共用）。
 *
 * 契约源：dev-docs `03-数据字典/20-内容.md` §1 `edition_label` + `02-API契约/A6-书籍域.md`
 * §16.1 / §16.2 / §17：自由短文本、非 enum 闭集；非空时 trim 后长度 `1..32`，否则 `VALIDATION_FAILED`；
 * 落库存 trim 后的值。
 *
 * **trim 口径**：`String.prototype.trim`（Zod `.trim()` 同一实现）。DB CHECK `novels_edition_label_valid`
 * 的空白字符类按同一集合生成（server `~/lib/novel-edition-label.ts`），两侧同集才不会出现「入参放行、
 * CHECK 拒成 500」。
 *
 * **长度口径**：按 Unicode 码点计，与列类型 `varchar(32)` / PG `char_length` 同单位 —— 用 JS
 * `.length`（UTF-16 码元）会把合法的 32 字符标签（含 BMP 外字符）误拒。
 */

import { z } from 'zod';

/** trim 后长度上界（= 列类型 `varchar(32)`）。 */
export const EDITION_LABEL_MAX_LENGTH = 32;

/** 标签长度（Unicode 码点数，与 PG `char_length` 同单位）。 */
export function editionLabelLength(value: string): number {
  return Array.from(value).length;
}

/**
 * 写入端点收的标签串：trim 后长度 `1..32`，输出 trim 后的值。
 * 「可省略」「可置 null」按各端点契约在调用处叠加（§16.2 可 null，§16.1 / §17 只可省略）。
 */
export const EditionLabelSchema = z
  .string()
  .trim()
  .refine(
    (value) => {
      const length = editionLabelLength(value);
      return length >= 1 && length <= EDITION_LABEL_MAX_LENGTH;
    },
    { message: `edition_label must be 1..${EDITION_LABEL_MAX_LENGTH} characters after trim` },
  );

/**
 * 表单草稿 → 提交值（Web 录入面用）：trim 后空串 = 未标注（原版，`null`）；超长 = 不可提交。
 *
 * 表单语义与 API 语义分开：API 收到「trim 后为空的非 null 串」一律 `VALIDATION_FAILED`，
 * 表单里的空输入框表达的是「原版」，故在这里归一成 `null` 再决定下发与否。
 */
export function parseEditionLabelDraft(
  raw: string,
): { ok: true; value: string | null } | { ok: false; reason: 'TOO_LONG' } {
  const trimmed = raw.trim();
  if (trimmed === '') return { ok: true, value: null };
  if (editionLabelLength(trimmed) > EDITION_LABEL_MAX_LENGTH)
    return { ok: false, reason: 'TOO_LONG' };
  return { ok: true, value: trimmed };
}
